import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import EventsManager from '@/components/admin/EventsManager';
import UsersViewer from '@/components/admin/UsersViewer';
import AdminStats from '@/components/admin/AdminStats';
import ListingRequests from '@/components/admin/ListingRequests';
import CategoryManager from '@/components/admin/CategoryManager';
import VisitLogs from '@/components/admin/VisitLogs';
import { LogOut, LayoutDashboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import TandemLogo from '@/assets/tandem-logo.svg';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPanel() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [activeTab, setActiveTab] = useState('stats');
  const [listingToApprove, setListingToApprove] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if admin credentials exist in sessionStorage
    const storedAuth = sessionStorage.getItem('admin_auth');
    if (storedAuth) {
      setCredentials(JSON.parse(storedAuth));
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    const username = e.target.username.value;
    const password = e.target.password.value;

    try {
      // Test authentication by calling admin endpoint
      const response = await fetch(`${BACKEND_URL}/api/admin/stats`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        }
      });

      if (response.ok) {
        const authData = { username, password };
        sessionStorage.setItem('admin_auth', JSON.stringify(authData));
        setCredentials(authData);
        setIsAuthenticated(true);
        toast.success('Login successful!');
      } else {
        toast.error('Invalid credentials');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed. Please try again.');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_auth');
    setIsAuthenticated(false);
    setCredentials({ username: '', password: '' });
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleApproveListingRequest = (listing) => {
    setListingToApprove(listing);
    setActiveTab('events');
  };

  if (isLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
          <p className="text-sm text-muted-foreground mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen w-full gradient-hero flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 inline-flex items-center justify-center gradient-primary rounded-2xl p-4 shadow-primary">
              <LayoutDashboard className="h-10 w-10 text-primary-foreground" />
            </div>
            <CardTitle className="text-2xl">Admin Login</CardTitle>
            <CardDescription>Enter your credentials to access the admin panel</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="username" className="text-sm font-medium">Username</label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter username"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium">Password</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter password"
                />
              </div>
              <Button type="submit" className="w-full gradient-primary text-primary-foreground hover:opacity-90">
                Login
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Button variant="ghost" onClick={() => navigate('/')} className="text-sm">
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <img 
                src={TandemLogo} 
                alt="Tandem Logo" 
                className="h-10 w-auto"
              />
              <div>
                <h1 className="text-xl font-bold text-foreground">Admin Panel</h1>
                <p className="text-xs text-muted-foreground">Manage events and users</p>
              </div>
            </div>
            <Button variant="outline" onClick={handleLogout} className="flex items-center space-x-2">
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-6 max-w-4xl">
            <TabsTrigger value="stats">Dashboard</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
            <TabsTrigger value="listings">Listing Requests</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="visits">Visit Logs</TabsTrigger>
            <TabsTrigger value="categories">Categories</TabsTrigger>
          </TabsList>

          <TabsContent value="stats">
            <AdminStats credentials={credentials} />
          </TabsContent>

          <TabsContent value="events">
            <EventsManager credentials={credentials} listingToApprove={listingToApprove} onListingProcessed={() => setListingToApprove(null)} />
          </TabsContent>

          <TabsContent value="listings">
            <ListingRequests credentials={credentials} onApprove={handleApproveListingRequest} />
          </TabsContent>

          <TabsContent value="users">
            <UsersViewer credentials={credentials} />
          </TabsContent>

          <TabsContent value="visits">
            <VisitLogs credentials={credentials} />
          </TabsContent>

          <TabsContent value="categories">
            <CategoryManager credentials={credentials} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
